/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { mutateVehiclesACES } from '@/features/ymme/api/postVehiclesACES';
import { mutateVehicles } from '@/features/ymme/api/postVehicles';
import { type QueryClient } from '@tanstack/react-query';
import { type AxiosInstance } from 'axios';
import logger from '@/utils/logger';
import { type Locale } from '@/types/i18n';
import { pageLinksToRepairGuides } from '@/features/myVehicle/utils/pageLinksToRepairGuides';

type ContextInfo = {
  pageName: 'PDP' | 'BrandShelf' | 'Search' | 'SEOUrlPath';
};

/**
 * Adds a vehicle to the user's vehicle list based on query parameters
 * Handles both ACES and non-ACES vehicle addition
 */
export const addVehicleFromQueryParams = async (
  queryClient: QueryClient,
  query: Record<string, string | string[] | undefined>,
  locale: string,
  axios: AxiosInstance,
  asPath: string,
  acesFlags: { acesEnabled: boolean; acesTransitionPhase?: boolean },
  contextInfo: ContextInfo,
): Promise<void> => {
  const { pageName } = contextInfo;
  const { acesEnabled, acesTransitionPhase } = acesFlags;

  if (acesTransitionPhase) {
    return;
  }

  if (acesEnabled) {
    try {
      // Helper function to decode URL-encoded values
      const formatNameField = (field: string | string[] | undefined): string => {
        if (!field) {
          return '';
        }
        return decodeURIComponent(String(field));
      };

      await mutateVehiclesACES(
        queryClient,
        {
          acesTransitionFlag: false,
          acesVehicleAttributes: {
            engineIds: {},
            partsProVehicleId: query.vehicleId ? String(query.vehicleId) : undefined,
            year: String(query.year ?? ''),
            makeId: String(query.makeId ?? ''),
            makeName: formatNameField(query.makeName),
            modelId: String(query.modelId ?? ''),
            modelName: formatNameField(query.modelName),
            vehicleTypeId: String(query.vehicleTypeId),
            vehicleTypeName: formatNameField(query.vehicleTypeName),
          },
          includeRepairGuides: pageLinksToRepairGuides(asPath),
          locale: locale as Locale,
        },
        axios,
      );
    } catch {
      logger.error({
        message: `Error when adding vehicle from query param in ${pageName}`,
        meta: {
          url: asPath,
        },
      });
    }
  } else {
    // ACES is disabled - use non-ACES API only if vehicleId is valid
    if (query.vehicleId && query.vehicleId !== '0') {
      try {
        await mutateVehicles(
          queryClient,
          {
            vehicleId: String(query.vehicleId),
            locale: locale as Locale,
          },
          axios,
        );
      } catch {
        logger.error({
          message: `Error when adding vehicle ${String(
            query.vehicleId,
          )} from query param in ${pageName}`,
          meta: {
            url: asPath,
          },
        });
      }
    }
  }
};
